import React, { useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { FaCopy, FaFilePdf } from "react-icons/fa";

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const BRAND      = "#FEC025";
const BRAND_DARK = "#C9960E";
const DOC_BG     = "#ffffff";
const HEADER_FG  = "#1a1a1a";
const BODY_FG    = "#2d2d2d";
const BORDER     = "#d0d0d0";
const ROW_ALT    = "#fafafa";
const SECTION_CAP = "#3a3a3a";
const FONT = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return String(val);
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, "0");
  const dd   = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatCurrency = (val) => {
  const n = Number(val);
  if (isNaN(n) || val === "" || val == null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SectionHeader = ({ children }) => (
  <div style={{
    background: BRAND,
    borderLeft: `5px solid ${BRAND_DARK}`,
    padding: "9px 14px",
    fontFamily: FONT,
    fontWeight: 700,
    fontSize: "12.5px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: HEADER_FG,
  }}>
    {children}
  </div>
);

const DateBadge = ({ label, value, bg }) => (
  <div style={{ textAlign: "center", minWidth: "170px" }}>
    <div style={{ fontSize: "11px", color: "#666", marginBottom: "5px", fontFamily: FONT }}>{label}</div>
    <div style={{
      background: bg,
      border: `1px solid ${BRAND_DARK}`,
      borderRadius: "4px",
      padding: "5px 20px",
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: "13px",
      color: HEADER_FG,
    }}>
      {value || "—"}
    </div>
  </div>
);

const DocTable = ({ children, style }) => (
  <table style={{ borderCollapse: "collapse", border: "none", width: "100%", fontFamily: FONT, fontSize: "12.5px", ...style }}>
    {children}
  </table>
);

const TH = ({ children, style }) => (
  <th style={{
    background: "#f0f0f0",
    border: "none",
    padding: "7px 10px",
    fontWeight: 600,
    fontSize: "11.5px",
    color: SECTION_CAP,
    textAlign: "center",
    letterSpacing: "0.3px",
    ...style,
  }}>
    {children}
  </th>
);

const TD = ({ children, center, bold, alt, style }) => (
  <td style={{
    border: "none",
    padding: "7px 10px",
    verticalAlign: "top",
    backgroundColor: alt ? ROW_ALT : DOC_BG,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    textAlign: center ? "center" : "left",
    fontWeight: bold ? 700 : 400,
    color: BODY_FG,
    fontFamily: FONT,
    fontSize: "12.5px",
    ...style,
  }}>
    {children}
  </td>
);

// ---------------------------------------------------------------------------
// WeeklyUpdatePreview (Development Manager)
// Props:
//   visible  – boolean
//   onHide   – () => void
//   values   – formik.values snapshot
// ---------------------------------------------------------------------------
const WeeklyUpdatePreview = ({ visible, onHide, values }) => {
  const previewRef = useRef(null);
  const toast      = useRef(null);
  const [isCopyLoading, setIsCopyLoading] = useState(false);
  const [isPdfLoading,  setIsPdfLoading]  = useState(false);

  // ── capture ──────────────────────────────────────────────────────────────
  const capturePreview = useCallback(async (pixelRatio = 2) => {
    if (!previewRef.current) return null;
    return await toPng(previewRef.current, { cacheBust: true, pixelRatio, backgroundColor: "#ffffff" });
  }, []);

  const handleCopy = useCallback(async () => {
    setIsCopyLoading(true);
    try {
      const imgSrc = await capturePreview(2);
      if (!imgSrc) { setIsCopyLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
      const canvas = document.createElement("canvas");
      canvas.width = img.width; canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      canvas.toBlob((blob) => {
        if (!blob) { setIsCopyLoading(false); return; }
        if (navigator.clipboard?.write) {
          navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })])
            .then(() => toast.current?.show({ severity: "success", summary: "Copied", detail: "Image copied to clipboard" }))
            .catch(() => toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to copy to clipboard" }))
            .finally(() => setIsCopyLoading(false));
        } else {
          toast.current?.show({ severity: "warn", summary: "Unavailable", detail: "Clipboard API not supported" });
          setIsCopyLoading(false);
        }
      }, "image/png");
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to capture preview" });
      setIsCopyLoading(false);
    }
  }, [capturePreview]);

  const handleDownloadPdf = useCallback(async () => {
    setIsPdfLoading(true);
    try {
      const imgSrc = await capturePreview(1.5);
      if (!imgSrc) { setIsPdfLoading(false); return; }
      const img = new Image();
      img.src = imgSrc;
      await new Promise((res) => { img.onload = res; });

      const pdf       = new jsPDF("p", "mm", "a4");
      const pdfW      = pdf.internal.pageSize.getWidth();
      const pdfH      = pdf.internal.pageSize.getHeight();
      const pageCanvH = (pdfH * img.width) / pdfW;

      const full = document.createElement("canvas");
      full.width = img.width; full.height = img.height;
      const fc = full.getContext("2d");
      fc.fillStyle = "#fff"; fc.fillRect(0, 0, full.width, full.height);
      fc.drawImage(img, 0, 0);

      let left = img.height, curY = 0, page = 0;
      while (left > 0) {
        if (page++ > 0) pdf.addPage();
        const sliceH = Math.min(left, pageCanvH);
        const pc = document.createElement("canvas");
        pc.width = img.width; pc.height = sliceH;
        const ctx = pc.getContext("2d");
        ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, pc.width, pc.height);
        ctx.drawImage(full, 0, curY, img.width, sliceH, 0, 0, img.width, sliceH);
        pdf.addImage(pc.toDataURL("image/jpeg", 0.82), "JPEG", 0, 0, pdfW, (sliceH * pdfW) / img.width);
        left -= sliceH; curY += sliceH;
      }

      const now = new Date();
      const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles", year: "numeric", month: "2-digit",
        day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const p = {}; fmt.formatToParts(now).forEach((x) => { p[x.type] = x.value; });
      const ts = `${p.year}-${p.month}-${p.day}_${p.hour}-${p.minute}`;
      const label = values?.name ? ` - ${values.name}` : "";
      pdf.save(`Development Manager Playbook - Weekly Update${label} - ${ts}.pdf`);
    } catch {
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to generate PDF" });
    } finally {
      setIsPdfLoading(false);
    }
  }, [capturePreview, values]);

  // ── footer ───────────────────────────────────────────────────────────────
  const dialogFooter = (
    <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
      <Button icon={<FaCopy />}    tooltip="Copy to Clipboard" tooltipOptions={{ position: "top" }} loading={isCopyLoading} onClick={handleCopy}        className="dm-btn-copy" type="button" />
      <Button icon={<FaFilePdf />} tooltip="Download PDF"      tooltipOptions={{ position: "top" }} loading={isPdfLoading}  onClick={handleDownloadPdf}  className="dm-btn-pdf"  type="button" />
    </div>
  );

  if (!values) return null;

  const weekFrom  = formatDate(values.hereIsYourWeeklySummaryFrom);
  const weekTo    = formatDate(values.hereIsYourWeeklySummaryTo);
  const weekRange = (weekFrom !== "—" || weekTo !== "—") ? `${weekFrom} — ${weekTo}` : "";

  const milestones    = values.section_3 || [];
  const activities    = values.section_4 || [];
  const budgetRows    = values.section_5 || [];
  const actionItems   = values.section_6 || [];

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <Dialog
      header="Weekly Update Preview — Development Manager Playbook"
      visible={visible}
      onHide={onHide}
      style={{ width: "92vw", maxWidth: "1180px" }}
      maximizable
      footer={dialogFooter}
      contentStyle={{ overflowY: "auto", padding: "20px", background: "#f2f2f2" }}
    >
      <Toast ref={toast} />

      <div style={{ maxWidth: "900px", margin: "0 auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", borderRadius: "6px", overflow: "hidden" }}>
      <div ref={previewRef} style={{
        background: DOC_BG,
        fontFamily: FONT,
        fontSize: "13px",
        color: BODY_FG,
        width: "900px",
        boxSizing: "border-box",
      }}>

        {/* ══ Document title block ════════════════════════════════════════ */}
        <div style={{
          background: BRAND,
          padding: "28px 32px 22px",
          borderBottom: `4px solid ${BRAND_DARK}`,
        }}>
          <div style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px", textTransform: "uppercase", color: "#7a5a00", marginBottom: "6px", fontFamily: FONT }}>
            Development Manager Playbook
          </div>
          <div style={{ fontSize: "24px", fontWeight: 800, color: "#1a1a1a", lineHeight: 1.2, marginBottom: "8px", fontFamily: FONT }}>
            Weekly Project Update
          </div>
          {values.name && (
            <div style={{ fontSize: "16px", fontWeight: 600, color: "#3a2a00", marginBottom: "6px", fontFamily: FONT }}>
              {values.name}
            </div>
          )}
          {weekRange && (
            <div style={{ fontSize: "13px", color: "#5a3e00", fontFamily: FONT }}>
              Period:&nbsp;<strong>{weekRange}</strong>
            </div>
          )}
        </div>

        {/* ══ Body ════════════════════════════════════════════════════════ */}
        <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: "22px" }}>

          {/* ── Project Overview ────────────────────────────────────────── */}
          <div>
            <SectionHeader>Project Overview</SectionHeader>
            <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", background: DOC_BG, padding: "16px 18px" }}>

              {values.project_summary && (
                <div style={{ marginBottom: "16px" }}>
                  <div style={{ fontWeight: 700, fontSize: "11px", color: SECTION_CAP, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>
                    Project Summary
                  </div>
                  <div style={{ lineHeight: 1.7, fontSize: "13px", whiteSpace: "pre-wrap", color: BODY_FG, padding: "10px 14px", background: ROW_ALT, borderRadius: "4px", border: `1px solid ${BORDER}`, fontFamily: FONT }}>
                    {values.project_summary}
                  </div>
                </div>
              )}

              <div style={{ fontWeight: 700, fontSize: "11px", color: SECTION_CAP, marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: FONT }}>
                Schedule
              </div>
              <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
                <DateBadge label="Expected Start Date"  value={formatDate(values.expected_date)}       bg="#FFF9E0" />
                <DateBadge label="Purchase Close Date"  value={formatDate(values.purchase_close_date)} bg="#FFE8D6" />
                <DateBadge label="Projected DP Date"    value={formatDate(values.projected_dp_date)}   bg="#E8FFE8" />
              </div>
            </div>
          </div>

          {/* ── Schedule Milestones ─────────────────────────────────────── */}
          {milestones.length > 0 && (
            <div>
              <SectionHeader>Schedule Milestones</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
                <DocTable>
                  <thead>
                    <tr>
                      <TH style={{ textAlign: "left", width: "28%" }}>Milestone</TH>
                      <TH style={{ width: "13%" }}>Planned Completion</TH>
                      <TH style={{ width: "13%" }}>Forecast Completion</TH>
                      <TH style={{ width: "13%" }}>Actual Completion</TH>
                      <TH>Notes</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {milestones.map((item, i) => (
                      <tr key={i}>
                        <TD alt={i % 2 === 1} style={{ fontWeight: 500 }}>{item.schedule_milestones || ""}</TD>
                        <TD alt={i % 2 === 1} center>{formatDate(item.plan_completion_date)}</TD>
                        <TD alt={i % 2 === 1} center>{formatDate(item.current_forecast_completion_date)}</TD>
                        <TD alt={i % 2 === 1} center>{formatDate(item.actual_completion_date)}</TD>
                        <TD alt={i % 2 === 1}>{item.notes || ""}</TD>
                      </tr>
                    ))}
                  </tbody>
                </DocTable>
                <div style={{
                  background: BRAND, borderTop: `1px solid ${BRAND_DARK}`,
                  textAlign: "center", fontStyle: "italic", fontWeight: 700,
                  fontSize: "13px", padding: "10px", fontFamily: FONT, color: "#3a2a00",
                }}>
                  "A day late is a day stolen from a client's life"
                </div>
              </div>
            </div>
          )}

          {/* ── Activities Worked On This Week ──────────────────────────── */}
          {activities.length > 0 && (
            <div>
              <SectionHeader>Activities Worked On This Week</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
                <DocTable>
                  <thead>
                    <tr>
                      <TH style={{ textAlign: "left", width: "22%" }}>Activity</TH>
                      <TH style={{ width: "15%" }}>Contractor</TH>
                      <TH>Brief Scope of Work</TH>
                      <TH style={{ width: "14%" }}>Current Forecast Date</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((item, i) => (
                      <tr key={i}>
                        <TD alt={i % 2 === 1} style={{ fontWeight: 500 }}>{item.activities_worked_on_this_week || ""}</TD>
                        <TD alt={i % 2 === 1}>{item.contractor || ""}</TD>
                        <TD alt={i % 2 === 1}>{item.brief_scope_of_work || ""}</TD>
                        <TD alt={i % 2 === 1} center>{formatDate(item.current_f_c_date)}</TD>
                      </tr>
                    ))}
                  </tbody>
                </DocTable>
              </div>
            </div>
          )}

          {/* ── Project Budget ─────────────────────────────────────────── */}
          {budgetRows.length > 0 && (
            <div>
              <SectionHeader>Project Budget</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
                <DocTable>
                  <thead>
                    <tr>
                      <TH style={{ textAlign: "left" }}>Item</TH>
                      <TH style={{ width: "160px" }}>Amount</TH>
                    </tr>
                  </thead>
                  <tbody>
                    {budgetRows.map((item, i) => (
                      <tr key={i}>
                        <TD alt={i % 2 === 1}>{item.project_budget || ""}</TD>
                        <TD alt={i % 2 === 1} center bold>{formatCurrency(item.amount)}</TD>
                      </tr>
                    ))}
                  </tbody>
                </DocTable>
              </div>
            </div>
          )}

          {/* ── Action Items Discussed ─────────────────────────────────── */}
          {actionItems.length > 0 && (
            <div>
              <SectionHeader>Action Items Discussed</SectionHeader>
              <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", overflow: "hidden" }}>
                <DocTable>
                  <tbody>
                    {actionItems.map((item, i) => (
                      <tr key={i}>
                        <TD alt={i % 2 === 1} style={{ paddingLeft: "14px" }}>
                          <span style={{ color: BRAND_DARK, fontWeight: 700, marginRight: "8px", fontFamily: FONT }}>{i + 1}.</span>
                          {item.action_items_discussed || ""}
                        </TD>
                      </tr>
                    ))}
                  </tbody>
                </DocTable>
              </div>
            </div>
          )}

          {/* ── Document footer ─────────────────────────────────────────── */}
          <div style={{
            borderTop: `1px solid ${BORDER}`, paddingTop: "12px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            fontSize: "11px", color: "#888", fontFamily: FONT,
          }}>
            <span>Development Manager Playbook{values?.name ? ` — ${values.name}` : ""}</span>
            <span>Generated: {formatDate(new Date().toISOString())}</span>
          </div>

        </div>{/* /body */}
      </div>{/* /previewRef */}
      </div>{/* /outer wrapper */}
    </Dialog>
  );
};

export default WeeklyUpdatePreview;
